import { useEffect, useState } from 'react';
import { supabase } from '../../services/supabaseClient.js';
import { adminSystemEvents, adminListLearners } from '../../services/adminClient.js';
import { T, card, sectionTitle, PageHeader } from './ui.jsx';

export default function AdminSupport() {
  const [contacts, setContacts] = useState(undefined); // undefined=loading, []=none/blocked, [...]=rows
  const [fallbacks, setFallbacks] = useState(undefined);
  const [emailById, setEmailById] = useState({});
  const [openRow, setOpenRow] = useState(null);        // expanded fallback row index

  useEffect(() => {
    let c = false;
    // contact_submissions has no admin RLS policy → this returns [] under the
    // admin's auth. Attempt anyway; fall back to the scaffold.
    supabase.from('contact_submissions')
      .select('name, email, subject, message, status, created_at')
      .order('created_at', { ascending: false }).limit(50)
      .then(({ data }) => { if (!c) setContacts(data || []); })
      .catch(() => { if (!c) setContacts([]); });

    // Quiz-unavailable fallbacks + a learner-id → email map to label them.
    Promise.all([adminSystemEvents('quiz_unavailable'), adminListLearners()])
      .then(([rows, learners]) => {
        if (c) return;
        setFallbacks(rows || []);
        const m = {}; (learners || []).forEach(l => { m[l.id] = l.email; });
        setEmailById(m);
      })
      .catch(() => { if (!c) setFallbacks([]); });
    return () => { c = true; };
  }, []);

  const th = { textAlign: 'left', fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: T.slate, padding: '0 10px 8px' };
  const td = { fontSize: 12.5, color: T.ink, padding: '9px 10px', borderTop: `1px solid ${T.border}` };

  return (
    <div>
      <PageHeader title="Support" subtitle="System health and learner issues." />

      {/* Quiz fallback log — quiz-unavailable events from system_events */}
      <div style={{ ...card, marginBottom: 16, overflowX: 'auto' }}>
        <p style={{ ...sectionTitle, marginBottom: 4 }}>Quiz Fallback Log</p>
        <p style={{ fontSize: 12, color: T.slate, margin: '0 0 12px' }}>
          Each time a checkpoint couldn’t render. Click a row for the model’s raw output. Should stay near zero.
        </p>
        {fallbacks === undefined ? (
          <p style={{ fontSize: 13, color: T.slate, margin: 0 }}>Loading…</p>
        ) : fallbacks.length === 0 ? (
          <p style={{ fontSize: 13, color: T.emerald, margin: 0, fontWeight: 600 }}>No quiz fallbacks logged. ✓</p>
        ) : (
          <>
            {/* Where it fails — counts by directive, so the failing stage is obvious */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {Object.entries(fallbacks.reduce((a, r) => { const d = r.metadata?.directive || 'unknown'; a[d] = (a[d] || 0) + 1; return a; }, {}))
                .sort((a, b) => b[1] - a[1])
                .map(([d, n]) => (
                  <span key={d} style={{ fontSize: 11, fontWeight: 700, color: T.ink, background: '#f1f3f7', borderRadius: 99, padding: '4px 10px' }}>
                    {d}: <span style={{ color: T.coral }}>{n}</span>
                  </span>
                ))}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead><tr>
                <th style={th}>Learner</th><th style={th}>When</th><th style={th}>Lesson</th><th style={th}>Phase</th><th style={th}>Directive</th><th style={th}>Reason</th><th style={th}>Retries</th>
              </tr></thead>
              <tbody>
                {fallbacks.map((r, i) => {
                  const m = r.metadata || {};
                  const open = openRow === i;
                  const retries = `r${m.recheck_retry ?? '?'}/m${m.manual_retry ?? '?'}${m.in_remediation ? ' ·rem' : ''}`;
                  return (
                    <FallbackRow key={i} r={r} m={m} i={i} open={open}
                      onToggle={() => setOpenRow(open ? null : i)}
                      email={emailById[r.user_id] || (r.user_id ? `${r.user_id.slice(0, 8)}…` : 'anon')}
                      retries={retries} td={td} />
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Contact submissions */}
      <div style={{ ...card, marginBottom: 16 }}>
        <p style={{ ...sectionTitle, marginBottom: 12 }}>Contact Submissions</p>
        {contacts === undefined ? (
          <p style={{ fontSize: 13, color: T.slate, margin: 0 }}>Loading…</p>
        ) : contacts.length === 0 ? (
          <p style={{ fontSize: 13, color: T.slate, margin: 0, lineHeight: 1.5 }}>
            None visible. <code style={{ background: '#f1f3f7', padding: '1px 5px', borderRadius: 4 }}>contact_submissions</code> exists but is service-role read only — add an <code style={{ background: '#f1f3f7', padding: '1px 5px', borderRadius: 4 }}>is_admin()</code> SELECT policy (unlock migration) to surface them here.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {contacts.map((r, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{r.name || r.email}</span>
                  <span style={{ fontSize: 11, color: T.slate }}>{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <p style={{ fontSize: 12.5, color: T.slate, margin: '3px 0 0' }}>{r.subject ? `${r.subject} — ` : ''}{r.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System status */}
      <div style={card}>
        <p style={{ ...sectionTitle, marginBottom: 12 }}>System Status</p>
        {[['Supabase connection', 'live'], ['OpenAI API', 'live'], ['Adzuna feed', 'live']].map(([n, s]) => (
          <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: T.emerald, boxShadow: '0 0 0 3px rgba(5,150,105,0.18)' }} />
            <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{n}</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: T.emerald }}>{s}</span>
          </div>
        ))}
        <p style={{ fontSize: 11.5, color: T.slate, margin: '12px 0 0' }}>Static indicators — live ping/latency checks are a later pass.</p>
      </div>
    </div>
  );
}

// One fallback row + an expandable diagnostic panel: artifact-delimiter flags,
// retry context, and the model's raw output (content_head) so the failure mode
// is readable at a glance (empty JSON, malformed delimiters, prose, etc.).
function FallbackRow({ r, m, open, onToggle, email, retries, td }) {
  const reasonColor = m.reason === 'malformed_artifact' ? T.coral : T.amber;
  const flags = [
    ['opened [QUIZ_JSON_START]', m.artifact_opened],
    ['closed [QUIZ_JSON_END]',   m.artifact_closed],
    ['malformed close [/…]',     m.bad_close],
    ['emitted prose',            m.emitted_prose],
  ];
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td style={td}>{email}</td>
        <td style={{ ...td, color: T.slate }}>{new Date(r.created_at).toLocaleString()}</td>
        <td style={td}>{m.lesson_title || m.lesson_id || '—'}{m.lesson_no != null ? ` (#${m.lesson_no})` : ''}</td>
        <td style={{ ...td, color: T.slate }}>{m.phase || '—'}</td>
        <td style={{ ...td, fontWeight: 700 }}>{m.directive || '—'}</td>
        <td style={{ ...td, color: reasonColor, fontWeight: 700 }}>{(m.reason || '—').replace('_artifact', '')}</td>
        <td style={{ ...td, color: T.slate, fontSize: 11 }}>{retries}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} style={{ padding: '0 10px 12px' }}>
            <div style={{ background: '#0f1117', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
                {flags.map(([label, val]) => (
                  <span key={label} style={{ fontSize: 11, fontWeight: 700, color: val ? '#fbbf24' : '#5b6472' }}>{val ? '●' : '○'} {label}</span>
                ))}
                <span style={{ fontSize: 11, color: '#8b93a1' }}>
                  len {m.content_len ?? '?'} · attempt {m.attempt ?? '?'} · session {(m.learning_session_id || '').slice(0, 8) || '—'}
                </span>
              </div>
              {m.parse_error && (
                <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#f87171', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  JSON.parse → {m.parse_error}
                </p>
              )}
              <pre style={{ margin: 0, maxHeight: 300, overflow: 'auto', fontSize: 11.5, lineHeight: 1.5, color: '#d6dae2', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                {m.content_head || '(no raw output captured — pre-existing event before this logging shipped)'}
              </pre>
              {m.content_tail && m.content_len > 3000 && (
                <pre style={{ margin: '8px 0 0', maxHeight: 160, overflow: 'auto', fontSize: 11.5, lineHeight: 1.5, color: '#9aa3b2', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, Menlo, monospace', borderTop: '1px solid #2a2f3a', paddingTop: 8 }}>
                  …tail… {m.content_tail}
                </pre>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
